import React from "react";
import PropTypes from "prop-types";

import { H1, H2, H3, H4, Paragraph } from "components/core/Typography";
import { Image } from "@sparefoot/react-commons";
import Link from "components/core/Link/Link";
import MarkdownContent from "components/molecules/MarkdownContent/MarkdownContent";

import "./PSPMarkdownContent.scss";

const extractTextContent = (children) => {
	if (typeof children === 'string') {
		return children;
	}
	if (Array.isArray(children)) {
		return children.map(extractTextContent).join('');
	}
	if (children?.props?.children) {
		return extractTextContent(children.props.children);
	}
	return '';
};

const getListItemKey = (child, index) => {
	const textContent = extractTextContent(child.props?.children);
	
	if (textContent && textContent.trim()) {
		const sanitizedText = textContent
			.trim()
			.substring(0, 50)
			.replace(/\s+/g, '-')
			.replace(/[^a-zA-Z0-9-]/g, '');
		return `li-${sanitizedText}`;
	}
	
	// Fallback: use index to ensure uniqueness
	return `li-${index}`;
};

const markdownContentMap = (additionalProps) => ({
	h1: (props) => (
		<H1
			size="large"
			weight="extraBold"
			{...additionalProps?.heading}
			{...props}
		/>
	),
	h2: (props) => (
		<H2
			size="large"
			weight="extraBold"
			{...additionalProps?.heading}
			{...props}
		/>
	),
	h3: (props) => (
		<H3
			size="medium"
			weight="extraBold"
			{...additionalProps?.heading}
			{...props}
		/>
	),
	h4: (props) => (
		<H4
			size="medium"
			{...additionalProps?.heading}
			{...props}
		/>
	),
	p: ({node,depth,...props}) => (
		<Paragraph
			size="standard"
			weight="regular"
			color="gray"
			{...additionalProps?.paragraph}
			{...props}
		/>
	),
	a: (props) => (
		<Link
			inheritFont
			{...additionalProps?.anchor}
			{...props}
		/>
	),
	img: (props) => <Image {...props} />,
	ul: ({ ordered, node, depth, ...props }) => (
		<ul
			className="psp-markdown-ul-padding"
			{...additionalProps?.ul}
			{...props}
		>
			{props.children && Array.isArray(props.children) ? (
				props.children.map((child, index) =>
					child && child.type === 'li' ? (
						<li key={getListItemKey(child, index)} style={additionalProps?.paragraph?.style}>
							{child.props.children}
						</li>
					) : (
						child
					)
				)
			) : (
				props.children
			)}
		</ul>
	),
	ol: ({ ordered,node,depth,...props }) => (
		<ol
			className="psp-markdown-ol"
			{...additionalProps?.ol}
			{...props}
		>
			{props.children && Array.isArray(props.children) ? (
				props.children.map((child, index) =>
					child && child.type === 'li' ? (
						<li key={getListItemKey(child, index)} style={additionalProps?.paragraph?.style}>
							{child.props.children}
						</li>
					) : (
						child
					)
				)
			) : (
				props.children
			)}
		</ol>
	)
});

// This is only a text component, use it for adding text into any PSP module
// It needs to be imported in a PSP module
export default function PSPMarkdownContent({ content, additionalProps,className=""}) {
	const markdownComponents = markdownContentMap(additionalProps);
	return (
		<MarkdownContent
			components={markdownComponents}
			content={content}
			className={className}
		/>
	);
}

PSPMarkdownContent.propTypes = {
	content: PropTypes.string.isRequired,
	additionalProps: PropTypes.object,
	className: PropTypes.string
};
